<template>
  <div class="chat-area print-keep">
    <MessageComponent
      :model="message"
      v-for="(message, index) in messages"
      :key="index"
      @open-feedback-modal="handleOpenFeedbackModal($event)"
      @copy-agent-response="handleEmit('copy')"
    />

    <SupportRequestButton
      v-if="props.model?.supportRequestOpenButtonVisible"
      class="support-button"
      v-show="supportRequestButtonVisible"
      @close="closeSupportRequestButton"
    />
  </div>

  <div
    :class="
      chatStore.isFirstQuestionSent
        ? 'sticky-question-with-footer-section'
        : 'question-with-footer-section'
    "
  >
    <Question
      :model="props.model?.question"
      @question-sent="handleQuestionSent"
      @answer-received="handleAnswerReceived"
      @answer-error="handleAnswerError"
      :isValid="isProductSelectValid"
      :key="componentKey"
    />
    <FooterComponent
      v-if="chatStore.isFirstQuestionSent"
      :model="props?.footer"
      :key="componentKey"
    />
  </div>
</template>

<script setup lang="ts">
import { updateComponentKey } from '../../composables/ComponentKey'
import type { Message } from '../../types/chat/Message'
import MessageComponent from './Message.vue'
import Question from './Question.vue'
import type { Chat } from '../../types/configuration/Chat'
import { MessageType } from '../../types/chat/MessageType'
import { parseMarkdownAsync } from '../../composables/ChatFormatting'
import SupportRequestButton from '../supportRequest/SupportRequestButton.vue'
import { t } from '../../composables/Translation'
import FooterComponent from '../../components/Footer.vue'
import type { Footer } from '../../types/configuration/Footer'
import { useChatStore } from '../../stores/chatStore'
import type { FeedbackGrade } from '../../types/FeedbackGrade'
import { storeToRefs } from 'pinia'
import type { MessageAttachment } from '../../types/chat/MessageAttachment'
import { useAppStore } from '../../stores/appStore'
import { getIsProductSelectValid } from '../../composables/ProductSelector'
import { computed } from 'vue'

const props = defineProps<{
  model: Chat | undefined
  footer: Footer | undefined
}>()

let componentKey = updateComponentKey(props)
const chatStore = useChatStore()
const appStore = useAppStore()

const { messages } = storeToRefs(chatStore)

const { supportRequestButtonVisible } = storeToRefs(appStore)

const isProductSelectValid = computed(() => {
  return getIsProductSelectValid()
})

function showSupportButton() {
  supportRequestButtonVisible.value = true
}

function closeSupportRequestButton() {
  supportRequestButtonVisible.value = false
}

function handleEmit(emitSignal: string) {
  if (emitSignal === 'copy') {
    emit('copy-agent-response')
  }
}

function handleOpenFeedbackModal(grade: FeedbackGrade) {
  emit('open-feedback-modal', grade)
}

const emit = defineEmits<{
  (e: 'copy-agent-response'): void
  (e: 'open-feedback-modal', grade: FeedbackGrade): void
}>()

async function handleQuestionSent(question: string, files?: MessageAttachment[]): Promise<void> {
  closeSupportRequestButton()

  const attachmentsToSend = (a: MessageAttachment, i: number): MessageAttachment => ({
    id:
      a.id ??
      (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${a.name}-${Date.now()}-${i}`),
    name: a.name,
    type: a.type,
    thumbnailUrl: a.thumbnailUrl,
    file: a.type === 'image' ? a.file : undefined,
  })

  const parsedQuestion = await parseMarkdownAsync(question)

  chatStore.pushMessage({
    avatar: props.model?.user.avatar,
    text: parsedQuestion,
    type: MessageType.User,
    alt: t('chat.user.avatar.alt'),
    isLoading: false,
    attachments: files ? files.map(attachmentsToSend) : undefined,
  } as Message)

  chatStore.pushMessage({
    avatar: props.model?.agent.avatar,
    text: '',
    type: MessageType.Agent,
    alt: t('chat.agent.avatar.alt'),
    isLoading: true,
  } as Message)

  chatStore.increaseQuestionNumber()
  scrollToThePageBottom()
}
async function handleAnswerReceived(answer: string): Promise<void> {
  const parsedMessage = await parseMarkdownAsync(answer)
  const lastIndex = messages.value.length - 1

  if (lastIndex >= 0) {
    chatStore.updateMessageWithAnswer(lastIndex, parsedMessage)
    showSupportButton()
    scrollToThePageBottom()
  }
}

async function handleAnswerError(errorMessage?: string): Promise<void> {
  const lastIndex = messages.value.length - 1
  const errorText = errorMessage || t('chat.questionError')

  const parsedError = await parseMarkdownAsync(errorText)

  if (lastIndex >= 0 && messages.value[lastIndex]?.type === MessageType.Agent) {
    chatStore.updateMessageWithError(lastIndex, errorText)
  } else {
    chatStore.pushMessage({
      avatar: props.model?.agent.avatar,
      text: parsedError,
      type: MessageType.Agent,
      alt: t('chat.agent.avatar.alt'),
      isLoading: false,
      isError: true,
    } as Message)
  }

  showSupportButton()
  scrollToThePageBottom()
}

function scrollToThePageBottom() {
  setTimeout(() => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth',
    })
  }, 100) // wrapped in the setTimeout to wait for dom loading
}
</script>

<style scoped lang="scss">
.chat-area {
  border-color: var(--background-color);
  display: flex;
  padding-top: 16px;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;
  padding-bottom: 32px;
}

.chat-area:has(div.user-message) {
  padding-bottom: 145px;
}

.support-button {
  align-self: center;
}

.sticky-question-with-footer-section {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 768px;
  /* Optional styling */
  background: var(--background-color);
  padding: 10px;
  text-align: center;
  z-index: 1000; /* stays on top */
  left: 50%;
  transform: translateX(-50%);
}

.question-with-footer-section {
  width: 100%;
}

@media (max-width: 992px) {
  .sticky-question-with-footer-section {
    width: 100%;
    padding-left: 12px;
    padding-right: 12px;
  }
}

@media print {
  .support-button,
  .sticky-question-with-footer-section {
    display: none !important;
  }

  .question-with-footer-section {
    display: none !important;
  }

  .chat-area {
    padding-top: 0 !important;
    align-items: stretch !important; /* let messages span full width */
  }
}
</style>
